import { Controller } from '@hotwired/stimulus'

const MASS_DECIMAL_PLACES = 6
const MASS_SCALE = 10 ** MASS_DECIMAL_PLACES
const MASS_PATTERN = new RegExp(`^-?\\d+(?:[.,]\\d{0,${MASS_DECIMAL_PLACES}})?$`)

const parseMassToScaledInteger = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.')

  if (normalized === '' || !MASS_PATTERN.test(normalized)) {
    return null
  }

  const isNegative = normalized.startsWith('-')
  const unsigned = isNegative ? normalized.slice(1) : normalized
  const [wholePart, decimalPart = ''] = unsigned.split('.')

  const whole = Number.parseInt(wholePart, 10)
  if (Number.isNaN(whole)) {
    return null
  }

  const decimals = Number.parseInt(decimalPart.padEnd(MASS_DECIMAL_PLACES, '0'), 10)
  if (Number.isNaN(decimals)) {
    return null
  }

  const scaled = whole * MASS_SCALE + decimals
  return isNegative ? -scaled : scaled
}

const formatScaledMass = (value: number): string => {
  const isNegative = value < 0
  const abs = Math.abs(value)

  const whole = Math.trunc(abs / MASS_SCALE)
  const decimals = `${abs % MASS_SCALE}`.padStart(MASS_DECIMAL_PLACES, '0').replace(/0+$/, '')
  const mass = decimals === '' ? `${whole}` : `${whole}.${decimals}`

  return isNegative ? `-${mass}` : mass
}

type DeviationField = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

export default class extends Controller {
  static targets = [
    'deviationPanel',
    'deviationToggle',
    'house',
    'housePartial',
    'houseItems',
    'item',
    'itemPartial',
    'itemGross',
    'houseGross',
    'totalGross'
  ]

  declare readonly hasDeviationPanelTarget: boolean
  declare readonly hasDeviationToggleTarget: boolean
  declare readonly deviationPanelTargets: HTMLElement[]
  declare readonly deviationToggleTarget: HTMLInputElement
  declare readonly hasTotalGrossTarget: boolean
  declare readonly houseTargets: HTMLElement[]
  declare readonly housePartialTargets: HTMLElement[]
  declare readonly houseItemsTargets: HTMLElement[]
  declare readonly itemTargets: HTMLElement[]
  declare readonly itemPartialTargets: HTMLElement[]
  declare readonly itemGrossTargets: HTMLInputElement[]
  declare readonly houseGrossTargets: HTMLInputElement[]
  declare readonly totalGrossTarget: HTMLInputElement

  connect(): void {
    this.syncDeviationPanel()
    this.recalculateHousesWithItemDeviations()
    this.recalculateTotalGross()
  }

  deviationToggleChanged(): void {
    this.syncDeviationPanel()
  }

  houseReceiptStateChanged(event: Event): void {
    const source = event.target as HTMLInputElement | null
    if (source?.dataset.houseSeq == null) return

    this.syncHouse(source.dataset.houseSeq, source.value)
    this.recalculateTotalGross()
  }

  itemReceiptStateChanged(event: Event): void {
    const source = event.target as HTMLInputElement | null
    if (source?.dataset.houseSeq == null || source.dataset.itemKey == null) return

    this.syncItem(source.dataset.houseSeq, source.dataset.itemKey, source.value)
    this.syncHouseReceiptStateFromItems(source.dataset.houseSeq)
    this.recalculateHouseGross(source.dataset.houseSeq)
    this.recalculateTotalGross()
  }

  itemGrossChanged(event: Event): void {
    const source = event.target as HTMLInputElement | null
    const houseSeq = source?.dataset.houseSeq

    if (houseSeq == null || houseSeq === '') {
      return
    }

    this.recalculateHouseGross(houseSeq)
    this.recalculateTotalGross()
  }

  houseGrossChanged(): void {
    this.recalculateTotalGross()
  }

  private recalculateHousesWithItemDeviations(): void {
    this.houseTargets.forEach(house => {
      const houseSeq = house.dataset.houseSeq
      if (houseSeq == null || houseSeq === '') return

      const hasItemDeviation = this.itemTargets
        .filter(item => item.dataset.houseSeq === houseSeq)
        .some(item => this.checkedReceiptState(item) !== 'fully_received')

      if (hasItemDeviation) this.recalculateHouseGross(houseSeq)
    })
  }

  private recalculateHouseGross(houseSeq: string): void {
    const houseField = this.houseGrossTargets.find(target => target.dataset.houseSeq === houseSeq)
    if (houseField == null) {
      return
    }

    const houseItems = this.itemTargets.filter(target => target.dataset.houseSeq === houseSeq)
    if (houseItems.length === 0) {
      const legacyItems = this.itemGrossTargets.filter(
        target => target.dataset.houseSeq === houseSeq
      )
      const sum = legacyItems.reduce(
        (total, itemField) => total + (parseMassToScaledInteger(itemField.value) ?? 0),
        0
      )
      houseField.value = formatScaledMass(sum)
      return
    }
    const sum = houseItems.reduce((total, item) => {
      const state = this.checkedReceiptState(item)
      if (state === 'not_received') return total

      const itemField = this.itemGrossTargets.find(
        target =>
          target.dataset.houseSeq === houseSeq &&
          target.closest(`[data-${this.identifier}-target~="item"]`) === item
      )
      const value =
        state === 'partially_received'
          ? parseMassToScaledInteger(itemField?.value ?? '')
          : parseMassToScaledInteger(item.dataset.sourceGross ?? '')
      return total + (value ?? 0)
    }, 0)

    houseField.value = formatScaledMass(sum)
  }

  private recalculateTotalGross(): void {
    if (!this.hasTotalGrossTarget) {
      return
    }

    if (this.houseTargets.length === 0) {
      const sum = this.houseGrossTargets.reduce(
        (total, houseField) => total + (parseMassToScaledInteger(houseField.value) ?? 0),
        0
      )
      this.totalGrossTarget.value = formatScaledMass(sum)
      return
    }

    const sum = this.houseTargets.reduce((total, house) => {
      const state = this.checkedReceiptState(house)
      if (state === 'not_received') return total

      const houseField = this.houseGrossTargets.find(
        target => target.dataset.houseSeq === house.dataset.houseSeq
      )
      const value =
        state === 'partially_received'
          ? parseMassToScaledInteger(houseField?.value ?? '')
          : parseMassToScaledInteger(house.dataset.sourceGross ?? '')
      return total + (value ?? 0)
    }, 0)

    this.totalGrossTarget.value = formatScaledMass(sum)
  }

  private syncDeviationPanel(): void {
    if (!this.hasDeviationPanelTarget || !this.hasDeviationToggleTarget) {
      return
    }

    const deviationsEnabled = this.deviationToggleTarget.value === '1'
    this.deviationPanelTargets.forEach(panel => {
      panel.classList.toggle('hidden', !deviationsEnabled)
      this.setFieldsDisabled(panel, !deviationsEnabled)
    })

    if (deviationsEnabled) {
      this.houseTargets.forEach(house =>
        this.syncHouse(house.dataset.houseSeq ?? '', this.checkedReceiptState(house))
      )
    }
  }

  private syncHouse(houseSeq: string, state: string): void {
    const partiallyReceived = state === 'partially_received'
    this.housePartialTargets
      .filter(panel => panel.dataset.houseSeq === houseSeq)
      .forEach(panel => this.syncPanel(panel, partiallyReceived))
    this.houseItemsTargets
      .filter(panel => panel.dataset.houseSeq === houseSeq)
      .forEach(panel => {
        this.syncPanel(panel, partiallyReceived)
        if (partiallyReceived) {
          this.itemTargets
            .filter(item => item.dataset.houseSeq === houseSeq)
            .forEach(item =>
              this.syncItem(houseSeq, item.dataset.itemKey ?? '', this.checkedReceiptState(item))
            )
        }
      })
  }

  private syncItem(houseSeq: string, itemKey: string, state: string): void {
    this.itemPartialTargets
      .filter(panel => panel.dataset.houseSeq === houseSeq && panel.dataset.itemKey === itemKey)
      .forEach(panel => this.syncPanel(panel, state === 'partially_received'))
  }

  private syncHouseReceiptStateFromItems(houseSeq: string): void {
    const items = this.itemTargets.filter(item => item.dataset.houseSeq === houseSeq)
    if (
      items.length === 0 ||
      !items.every(item => this.checkedReceiptState(item) === 'not_received')
    ) {
      return
    }

    const house = this.houseTargets.find(target => target.dataset.houseSeq === houseSeq)
    const notReceived = house?.querySelector<HTMLInputElement>(
      'input[type="radio"][value="not_received"]'
    )
    if (notReceived == null) {
      return
    }

    notReceived.checked = true
    this.syncHouse(houseSeq, 'not_received')
  }

  private syncPanel(panel: HTMLElement, enabled: boolean): void {
    panel.classList.toggle('hidden', !enabled)
    this.setFieldsDisabled(panel, !enabled)
  }

  private setFieldsDisabled(panel: HTMLElement, disabled: boolean): void {
    panel.querySelectorAll<DeviationField>('input, textarea, select').forEach(field => {
      field.disabled = disabled
    })
  }

  private checkedReceiptState(container: HTMLElement): string {
    return (
      container.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.value ??
      'fully_received'
    )
  }
}
